import React from 'react';
import { Users, Calendar, CreditCard, X } from 'lucide-react';
import { Button } from '../common/Button';
import { ModalPortal } from '../common/ModalPortal';

// Interface for referred user data
interface ReferralUser {
  id: string;
  name: string;
  email: string;
  date: string;
  hasSubscribed: boolean;
  plan?: string;
  minutesEarned: number;
}

// Interface for referral statistics
interface ReferralStats {
  totalReferrals: number;
  subscribed: number;
  minutesEarned: number;
}

interface ReferralStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  stats: ReferralStats;
  referredUsers: ReferralUser[];
  formatDate: (dateString: string) => string;
  zIndex?: number;
}

const ReferralStatsModal: React.FC<ReferralStatsModalProps> = ({
  isOpen,
  onClose,
  stats,
  referredUsers,
  formatDate,
  zIndex = 10000
}) => {
  if (!isOpen) return null;

  return (
    <ModalPortal>
      <div 
        className="fixed inset-0 flex items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm overflow-auto"
        style={{ zIndex }}
      >
        <div className="w-full max-w-5xl bg-background-light dark:bg-background-dark rounded-xl border border-border-light dark:border-border-dark shadow-xl animate-modal-in overflow-hidden">
          <div className="sticky top-0 z-10 flex items-center justify-between p-4 md:p-6 border-b border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark">
            <h3 className="text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
              Referral Statistics
            </h3>
            <button 
              onClick={onClose}
              className="p-1 rounded-md text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-light dark:hover:bg-surface-dark transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="p-4 md:p-6">
            {/* Summary cards - using only 3 cards with full width */}
            <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="w-full p-4 bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10">
                    <Users className="w-5 h-5 text-primary-light dark:text-primary-dark" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Total Referrals</p>
                    <p className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark">{stats.totalReferrals}</p>
                  </div>
                </div>
              </div>
              
              <div className="w-full p-4 bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-amber-500/10 dark:bg-amber-500/10">
                    <CreditCard className="w-5 h-5 text-amber-500 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Subscribed</p>
                    <p className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark">{stats.subscribed}</p>
                  </div>
                </div>
              </div>
              
              <div className="w-full p-4 bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-blue-500/10 dark:bg-blue-500/10">
                    <Calendar className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Minutes Earned</p>
                    <p className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark">{stats.minutesEarned}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Table of referred users */}
            <div className="w-full overflow-x-auto rounded-lg border border-border-light dark:border-border-dark mb-4">
              <table className="w-full">
                <thead className="bg-surface-light dark:bg-surface-dark">
                  <tr>
                    <th className="p-3 text-left text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">User</th>
                    <th className="p-3 text-left text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">Date</th>
                    <th className="p-3 text-left text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">Status</th>
                    <th className="p-3 text-left text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">Plan</th>
                    <th className="p-3 text-right text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">Minutes Earned</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-light dark:divide-border-dark">
                  {referredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-surface-light/50 dark:hover:bg-surface-dark/50 transition-colors">
                      <td className="p-3 text-text-primary-light dark:text-text-primary-dark">
                        <div>
                          <p className="font-medium text-sm">{user.name}</p>
                          <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">{user.email}</p>
                        </div>
                      </td>
                      <td className="p-3 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                        {formatDate(user.date)}
                      </td>
                      <td className="p-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.hasSubscribed 
                            ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400' 
                            : 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400'
                        }`}>
                          {user.hasSubscribed ? 'Subscribed' : 'Signed Up'}
                        </span>
                      </td>
                      <td className="p-3 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                        {user.plan || '-'}
                      </td>
                      <td className="p-3 text-right">
                        <span className="text-base font-medium text-text-primary-light dark:text-text-primary-dark">
                          {user.minutesEarned} min
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <p className="w-full text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4">
              You have earned a total of {stats.minutesEarned} minutes of interview time from your referrals.
            </p>
            
            <div className="w-full flex justify-end">
              <Button
                variant="outline"
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default ReferralStatsModal; 